import React, { useState, useEffect } from 'react';
import MainLayout from '../components/MainLayout';
import BulkUploadModal from '../components/BulkUploadModal';
import { Calendar, Download, Upload, Users, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { downloadCSV } from '../lib/exportUtils';
import { useActiveCompany } from '../hooks/useActiveCompany';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_COLORS = {
  present: 'bg-green-50 text-green-700 border-green-200',
  absent: 'bg-red-50 text-red-700 border-red-200',
  leave: 'bg-blue-50 text-blue-700 border-blue-200',
  lop: 'bg-rose-50 text-rose-700 border-rose-200',
  half_day: 'bg-amber-50 text-amber-700 border-amber-200',
  weekly_off: 'bg-slate-50 text-slate-700 border-slate-200',
  holiday: 'bg-purple-50 text-purple-700 border-purple-200',
  on_duty: 'bg-teal-50 text-teal-700 border-teal-200',
  work_from_home: 'bg-indigo-50 text-indigo-700 border-indigo-200',
};

const DailyAttendance = () => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [selectedCompany] = useActiveCompany();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [employeeCount, setEmployeeCount] = useState(null); // null = not yet loaded
  const [employeeNameMap, setEmployeeNameMap] = useState({});
  const [totalRecordsForCompany, setTotalRecordsForCompany] = useState(null);
  const [mostRecentDateWithData, setMostRecentDateWithData] = useState(null);

  // Fetch employees + attendance records for the active company + selected date.
  useEffect(() => {
    if (!selectedCompany) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API_URL}/employees?company_id=${selectedCompany}`, { withCredentials: true }).catch(() => ({ data: [] })),
      axios.get(`${API_URL}/attendance/records?company_id=${selectedCompany}&attendance_date=${selectedDate}`, { withCredentials: true }).catch(() => ({ data: [] })),
      // Total count across ALL dates for this company — used to prompt the user when today is empty but other dates have data.
      axios.get(`${API_URL}/attendance/records?company_id=${selectedCompany}&limit=1`, { withCredentials: true }).catch(() => ({ data: [] })),
    ]).then(([empRes, attRes, allAttRes]) => {
      const emps = empRes.data || [];
      setEmployeeCount(emps.length);
      const map = {};
      emps.forEach(e => {
        const code = e.employment_details?.employee_code;
        const name = e.personal_details?.full_name
          || [e.personal_details?.first_name, e.personal_details?.last_name].filter(Boolean).join(' ')
          || code;
        if (code) map[code] = name;
      });
      setEmployeeNameMap(map);
      setRecords(attRes.data || []);
      // Heuristic: if today is empty, peek at any record to tell the user when data DOES exist.
      if ((attRes.data || []).length === 0 && (allAttRes.data || []).length > 0) {
        const firstRow = allAttRes.data[0];
        setMostRecentDateWithData(firstRow?.attendance_date || null);
        setTotalRecordsForCompany('at-least-one');
      } else {
        setMostRecentDateWithData(null);
        setTotalRecordsForCompany((allAttRes.data || []).length > 0 ? 'at-least-one' : 'none');
      }
    }).finally(() => setLoading(false));
  }, [selectedCompany, selectedDate]);

  const stats = records.reduce((acc, r) => {
    const s = r.attendance_status || 'present';
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  const refreshAfterUpload = () => {
    toast.success('Attendance records uploaded. Refreshing…');
    // Re-fetch by toggling the date effect: reset and re-set to force reload.
    const d = selectedDate; setSelectedDate(''); setTimeout(() => setSelectedDate(d), 50);
  };

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="daily-attendance-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
              <Calendar className="inline-block w-8 h-8 mr-2 mb-1" />
              Daily Attendance Register
            </h1>
            <p className="mt-1 text-sm text-slate-600">View and manage daily attendance records</p>
          </div>
          <div className="flex space-x-3">
            <Button variant="outline" data-testid="attendance-bulk-upload-btn" onClick={() => setShowBulkUpload(true)}>
              <Upload className="w-4 h-4 mr-2" />
              Bulk Upload
            </Button>
            <Button variant="outline" data-testid="daily-export-btn" onClick={() => {
              downloadCSV(records.map(r => ({
                Code: r.employee_code, Name: employeeNameMap[r.employee_code] || '', In: r.in_time || '', Out: r.out_time || '',
                Status: r.attendance_status, Remarks: r.remarks || '',
              })), `daily_attendance_${selectedDate}.csv`);
              toast.success('Daily attendance exported');
            }}>
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          </div>
        </div>

        {/* No-employees guard banner */}
        {employeeCount === 0 && !loading && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3" data-testid="daily-attendance-no-employees-banner">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-900">No employees in this company yet</p>
              <p className="text-xs text-amber-800 mt-0.5">
                You cannot upload attendance until at least one employee exists. Pick one of the options below
                — the easiest is to seed a batch of sample employees in one click so their codes match the ones in your attendance CSV
                (format: <span className="font-mono">HECS-CORP-2026-0001</span> … ).
              </p>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <Button
                  onClick={async () => {
                    if (!selectedCompany) return;
                    const n = parseInt(window.prompt('How many sample employees to create?', '286') || '0', 10);
                    if (!n || n < 1) return;
                    try {
                      const { data } = await axios.post(
                        `${API_URL}/employees/seed-sample?company_id=${selectedCompany}&count=${n}`,
                        {}, { withCredentials: true },
                      );
                      toast.success(`Created ${data.created_count} employees (${data.first_code} … ${data.last_code})`);
                      // trigger reload
                      const d = selectedDate; setSelectedDate(''); setTimeout(() => setSelectedDate(d), 50);
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'Seed failed');
                    }
                  }}
                  data-testid="seed-sample-employees-btn"
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <Users className="w-3.5 h-3.5 mr-1.5" /> Seed Sample Employees
                </Button>
                <Button
                  onClick={() => navigate('/employees')}
                  data-testid="goto-employees-btn"
                  variant="outline"
                  size="sm"
                >
                  Go to Employees
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Data-on-other-dates nudge — fires when today is empty but company has records */}
        {!loading && employeeCount > 0 && records.length === 0 && mostRecentDateWithData && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3" data-testid="daily-attendance-other-dates-banner">
            <Calendar className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-blue-900">
                No attendance for {selectedDate} — but your uploaded data is saved.
              </p>
              <p className="text-xs text-blue-800 mt-0.5">
                The most recent record in the database is for{' '}
                <button
                  onClick={() => setSelectedDate(mostRecentDateWithData)}
                  data-testid="jump-to-recent-date-btn"
                  className="underline font-semibold text-blue-700 hover:text-blue-900"
                >
                  {mostRecentDateWithData}
                </button>
                {' '}— click to view it, or pick any date in the range of your uploaded CSV.
              </p>
            </div>
          </div>
        )}

        {/* Date Selector & Stats */}
        <div className="flex items-center space-x-6">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Select Date</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              data-testid="date-selector"
              className="h-10 px-4 bg-white border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-[#0052FF] focus:border-[#0052FF]"
            />
          </div>
          <div className="flex space-x-4" data-testid="daily-attendance-stats">
            <Stat label="Present" value={stats.present || 0} color="text-green-600" />
            <Stat label="Absent" value={stats.absent || 0} color="text-red-600" />
            <Stat label="Leave" value={stats.leave || 0} color="text-blue-600" />
            <Stat label="LOP" value={stats.lop || 0} color="text-rose-600" />
            <Stat label="Half Day" value={stats.half_day || 0} color="text-amber-600" />
            <Stat label="Week Off" value={stats.weekly_off || 0} color="text-slate-600" />
            <Stat label="WFH" value={stats.work_from_home || 0} color="text-indigo-600" />
          </div>
        </div>

        {/* Attendance Table */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {['Employee Code', 'Name', 'In Time', 'Out Time', 'Status', 'Remarks'].map(h => (
                  <th key={h} className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="py-8 text-center text-sm text-slate-400">Loading…</td></tr>
              ) : records.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-10 text-center text-sm text-slate-500" data-testid="daily-attendance-empty">
                    {employeeCount === 0
                      ? 'Add employees first, then upload attendance.'
                      : `No attendance recorded for ${selectedDate}. Use Bulk Upload to add records.`}
                  </td>
                </tr>
              ) : records.map((r) => (
                <tr
                  key={r.id || r._id || `${r.employee_code}-${r.attendance_date}`}
                  data-testid={`attendance-row-${r.employee_code}`}
                  className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                >
                  <td className="py-3 px-4 text-sm text-slate-900 font-mono font-medium">{r.employee_code}</td>
                  <td className="py-3 px-4 text-sm text-slate-900 font-medium">{employeeNameMap[r.employee_code] || '—'}</td>
                  <td className="py-3 px-4 text-sm text-slate-700 font-mono">{r.in_time || '-'}</td>
                  <td className="py-3 px-4 text-sm text-slate-700 font-mono">{r.out_time || '-'}</td>
                  <td className="py-3 px-4 text-sm">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${STATUS_COLORS[r.attendance_status] || 'bg-slate-50 text-slate-700 border-slate-200'}`}>
                      {(r.attendance_status || '').replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-xs text-slate-500">{r.remarks || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <BulkUploadModal
          open={showBulkUpload}
          onClose={() => setShowBulkUpload(false)}
          title="Bulk Attendance Upload"
          description="Upload a CSV to log manual attendance for multiple employees. Accepted statuses: Present, Absent, Leave, LOP, Half Day, Week Off, Holiday, On Duty, WFH (short forms like P/A/L/HD/WO/H/OD also work). Date format: YYYY-MM-DD."
          templateUrl="/attendance/bulk-upload/template"
          uploadUrl="/attendance/bulk-upload"
          uploadParams={selectedCompany ? { company_id: selectedCompany } : {}}
          templateFileName="attendance_bulk_upload_template.csv"
          onUploadSuccess={refreshAfterUpload}
          columns={['employee_code', 'attendance_date', 'attendance_status', 'in_time', 'out_time', 'remarks']}
          supportsDryRun
        />
      </div>
    </MainLayout>
  );
};

const Stat = ({ label, value, color }) => (
  <div className="text-center">
    <p className={`text-2xl font-semibold ${color} font-mono`}>{value}</p>
    <p className="text-xs text-slate-500">{label}</p>
  </div>
);

export default DailyAttendance;
